import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { createUser, updateUser, getUsersList, getRoles } from '../../../api/administration';
import { toast } from 'react-toastify';
import UsersHeader from './components/UsersHeader';
import UsersTable from './components/UsersTable';
import UsersModal from './components/UsersModal';

// Map API login_type values to UI dropdown values
const mapApiLoginTypeToUI = (apiLoginType) => {
  if (!apiLoginType) return '';
  const loginType = apiLoginType.toLowerCase();
  const mapping = {
    'cognito': 'userid',
    'user id': 'userid',
    'userid': 'userid',
    'user_id': 'userid',
    'password': 'userid',
    'google': 'email',
    'google_oauth': 'email',
    'oauth': 'email',
    'email': 'email',
    'sso': 'sso',
  };
  return mapping[loginType] || 'userid';
};

// Map API role value to UI dropdown value (find matching role option)
const mapApiRoleToUI = (apiRole, roleOptions) => {
  if (!apiRole) return '';
  const apiRoleStr = String(apiRole);
  // Check if the role value exists in roleOptions (use String() to avoid type mismatch)
  const matchedRole = roleOptions.find(
    (opt) => String(opt.value) === apiRoleStr || opt.text?.toLowerCase() === apiRoleStr.toLowerCase()
  );
  return matchedRole ? matchedRole.value : apiRole;
};

// Map API status value to UI status value (case-insensitive match against config options)
const mapApiStatusToUI = (status, statusOptions) => {
  if (status !== undefined && status !== null) {
    const statusStr = String(status).toLowerCase();
    const matched = statusOptions.find((opt) => opt.value.toLowerCase() === statusStr);
    return matched ? matched.value : String(status);
  }
  return 'Active'; // Default
};

const Users = () => {
  const referenceData = useSelector((state) => state.reference);

  // Build dynamic status options from UserStatuses config
  const statusOptions = (() => {
    const userStatuses = referenceData?.UserStatuses?.status;
    if (Array.isArray(userStatuses) && userStatuses.length > 0) {
      return userStatuses.map((item) => ({
        value: item.status,
        text: item.status,
      }));
    }
    // Fallback if config not loaded yet
    return [
      { value: 'Active', text: 'Active' },
      { value: 'Inactive', text: 'Inactive' },
    ];
  })();

  const initialUser = {
    firstName: '',
    lastName: '',
    userName: '',
    email: '',
    role: '',
    loginType: '',
    userStatus: 'Active',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(initialUser);
  const [users, setUsers] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);
  const [roleOptions, setRoleOptions] = useState([{ value: '', text: '--Choose Role--' }]);

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await getRoles();
      // Roles fetched

      if (response && response.data) {
        // Handle different response structures
        let roles = [];
        const data = response.data;

        if (Array.isArray(data)) {
          roles = data;
        } else if (Array.isArray(data.roles)) {
          roles = data.roles;
        } else if (Array.isArray(data.data)) {
          roles = data.data;
        } else if (data.data && Array.isArray(data.data.records)) {
          roles = data.data.records;
        }

        if (roles.length > 0) {
          // Filter to active roles only (client-side safety net)
          const activeRoles = roles.filter((role) => {
            const status = role.status || role.role_status || '';
            return !status || status.toLowerCase() === 'active';
          });

          // Map roles per API spec: role_id → value, role → text
          const roleMap = new Map();
          activeRoles.forEach((role) => {
            const value = role.role_id ?? role.id ?? role.role ?? role;
            const text = role.role ?? role.role_name ?? role.name ?? role;
            // Only add if not already in map (prevents duplicates)
            if (!roleMap.has(value)) {
              roleMap.set(value, { value, text });
            }
          });

          const options = [
            { value: '', text: '--Choose Role--' },
            ...Array.from(roleMap.values()),
          ];
          setRoleOptions(options);
        }
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback to default options if API fails
      setRoleOptions([
        { value: '', text: '--Choose Role--' },
        { value: 'admin', text: 'Admin' },
        { value: 'manager', text: 'Manager' },
        { value: 'user', text: 'User' },
        { value: 'viewer', text: 'Viewer' },
      ]);
    }
  };

  const openModalForNewUser = () => {
    setIsEditMode(false);
    setEditingUserId(null);
    setCurrentUser(initialUser);
    setIsModalOpen(true);
  };

  const openModalForUpdate = (userId) => {
    const userData = users.find(
      (u) => u.user_id === userId || u.userId === userId || u.id === userId
    );
    if (!userData) {
      toast.error('User not found');
      return;
    }

    // Get API values for login_type, role, and status
    const apiLoginType = userData.login_type || userData.loginType || '';
    const apiRole = userData.role || userData.role_id || '';
    const apiStatus = userData.status;

    setIsEditMode(true);
    setEditingUserId(userData.user_id || userData.userId || userData.id);
    setCurrentUser({
      firstName: userData.first_name || userData.firstName || '',
      lastName: userData.last_name || userData.lastName || '',
      userName: userData.user_name || userData.username || userData.userName || '',
      email: userData.email_id || userData.email || '',
      role: mapApiRoleToUI(apiRole, roleOptions),
      loginType: mapApiLoginTypeToUI(apiLoginType),
      userStatus: mapApiStatusToUI(apiStatus, statusOptions),
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingUserId(null);
    setCurrentUser(initialUser);
  };

  // Map UI login type value to API login type value
  const mapLoginTypeForApi = (uiLoginType) => {
    const mapping = {
      'userid': 'COGNITO',
      'email': 'GOOGLE',
      'sso': 'SSO',
    };
    return mapping[uiLoginType] || 'COGNITO';
  };

  // Get role name from role value (use String() to avoid type mismatch with select values)
  const getRoleNameForApi = (roleValue) => {
    const roleStr = String(roleValue);
    const matchedRole = roleOptions.find((opt) => String(opt.value) === roleStr);
    return matchedRole ? matchedRole.text : roleValue;
  };

  // Send status value as-is to the API (matches config values directly)
  const mapStatusForApi = (uiStatus) => {
    return uiStatus || 'Active';
  };

  // Determine which form field caused a server error based on status code and message
  const parseFieldError = (error, isEdit) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message || 'Failed to save user';
    const msgLower = message.toLowerCase();

    // Network error (no response) — likely CORS-blocked 409/400 from backend.
    // Show inline errors on the most likely duplicate fields.
    if (!error.response) {
      const hint = 'User ID or Email may already exist. Please verify and try again.';
      if (isEdit) {
        return { fieldErrors: { email: hint } };
      }
      return { fieldErrors: { userName: hint, email: hint } };
    }

    if (status === 409) {
      if (msgLower.includes('email')) {
        return { fieldErrors: { email: message } };
      }
      // Default 409 = username duplicate
      return { fieldErrors: { userName: message } };
    }
    if (status === 400) {
      if (msgLower.includes('email')) return { fieldErrors: { email: message } };
      if (msgLower.includes('username') || msgLower.includes('user_name') || msgLower.includes('user id')) return { fieldErrors: { userName: message } };
      if (msgLower.includes('first')) return { fieldErrors: { firstName: message } };
      if (msgLower.includes('last')) return { fieldErrors: { lastName: message } };
      if (msgLower.includes('password')) return { fieldErrors: { userName: message } };
    }
    // Unrecognized error — show as toast, no field-level error
    return { toast: message };
  };

  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Form data received for processing

      const roleId = formData.role ? parseInt(formData.role, 10) : undefined;
      const roleName = getRoleNameForApi(formData.role);

      if (isEditMode) {
        const userData = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          roleId: roleId || undefined,
          role: roleName,
          loginType: mapLoginTypeForApi(formData.loginType),
          status: mapStatusForApi(formData.userStatus),
        };
        await updateUser(editingUserId, userData);
        toast.success('User Updated Successfully');
      } else {
        const userData = {
          userName: formData.userName.trim(),
          email: formData.email.trim().toLowerCase(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          roleId: roleId || undefined,
          role: roleName,
          loginType: mapLoginTypeForApi(formData.loginType),
          status: mapStatusForApi(formData.userStatus),
        };
        await createUser(userData);
        toast.success('User Created Successfully');
      }

      closeModal();
      getData();
      return null; // success — no errors
    } catch (error) {
      console.error('Error saving user:', error);
      const parsed = parseFieldError(error, isEditMode);
      if (parsed.fieldErrors) {
        // Return field errors to UsersModal so it can show them inline
        return parsed;
      }
      // Fallback: show as toast
      toast.error(parsed.toast);
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await updateUser(userId, { status: 'Deleted' });
      toast.success('User Deleted Successfully');
      getData();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to delete user';
      toast.error(errorMessage);
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      const response = await getUsersList(1, 100, 'ACTIVE');
      // Users data fetched

      if (response && response.data) {
        const data = response.data;
        // Handle structure: { message, data: { totalRecords, pageNum, pageSize, records } }
        const usersList = data.data?.records || data.records || data.users || [];
        // Users list extracted
        setUsers(Array.isArray(usersList) ? usersList : []);
      } else if (Array.isArray(response)) {
        setUsers(response);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  // Filter users based on search term (client-side filtering)
  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
    const email = (user.email_id || user.email || '').toLowerCase();
    return (
      fullName.includes(searchLower) ||
      user.user_name?.toLowerCase().includes(searchLower) ||
      user.username?.toLowerCase().includes(searchLower) ||
      email.includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="p-5">
      {/* Header with actions */}
      <div className="border border-gray-300 rounded-lg relative">
        <UsersHeader
          onReload={getData}
          onAddUser={openModalForNewUser}
          isLoading={isLoading}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Table with loading overlay */}
        <div className="relative">
          <UsersTable
            data={filteredUsers}
            onEditUser={openModalForUpdate}
            onDeleteUser={handleDeleteUser}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            roleOptions={roleOptions}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              {/* Spinner */}
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              {/* Loading text with animated dots */}
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* User Modal */}
      <UsersModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentUser}
        roleOptions={roleOptions}
        statusOptions={statusOptions}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Users;
